import logger from '../utils/logger';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useShop } from '../context/ShopContext';
import { useBaristaSettings } from '../context/BaristaSettingsContext';
import { useNotificationSound } from '../hooks/useNotificationSound';
import BaristaSettingsPanel from '../components/BaristaSettingsPanel';
import { getProductImageUrl } from '../utils/imageUtils';
import {
  Coffee,
  Plus,
  Minus,
  Trash2,
  ShoppingCart,
  Receipt,
  Clock,
  CheckCircle,
  AlertCircle,
  Table,
  X,
  Settings,
  LogOut
} from 'lucide-react';
import orderService from '../services/orderService';
import websocketService from '../services/websocketService';
import invoiceService from '../services/invoiceService';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const BaristaDashboard = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { shop, products, tables } = useShop();
  const { settings } = useBaristaSettings();
  const { playNotificationSound } = useNotificationSound();
  const [orders, setOrders] = useState([]);
  const [incomingOrders, setIncomingOrders] = useState([]);
  const [manualOrders, setManualOrders] = useState([]);
  // const [selectedStatus] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const notifiedOrderIdsRef = useRef(new Set());

  // Helper functions
  const getThemeClasses = () => {
    switch (settings.theme) {
      case 'light':
        return 'min-h-screen bg-gradient-to-b from-gray-100 to-gray-200';
      case 'coffee':
        return 'min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900';
      default:
        return 'min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827]';
    }
  };

  const getTextSizeClass = () => {
    switch (settings.fontSize) {
      case 'small':
        return 'text-sm';
      case 'large':
        return 'text-lg';
      default:
        return 'text-base';
    }
  };

  const fetchOrders = useCallback(async () => {
    try {
      const result = await orderService.getActiveOrders();
      if (result.success) {
        const allOrders = result.data;
        setOrders(allOrders);
        // Only show QR orders in incoming orders section
        setIncomingOrders(allOrders.filter(order => 
          order.origin === 'QR' && (order.status === 'CREATED' || order.status === 'PREPARING' || order.status === 'READY')
        ));
        // Show manual orders in separate section
        setManualOrders(allOrders.filter(order => 
          order.origin === 'MANUAL' && (order.status === 'CREATED' || order.status === 'PREPARING' || order.status === 'READY')
        ));
      } else {
        toast.error(result.error);
      }
    } catch (error) {
  logger.error('Failed to fetch orders:', error);
      toast.error(t('barista.failedToFetchOrders'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);


    useEffect(() => {
    if (shop && user) {
      const token = localStorage.getItem('token');
      if (token) {
        websocketService.connect(token).then(() => {
          logger.info('WebSocket connected successfully for shop:', shop.id);
          websocketService.subscribeToOrders(shop.id, (newOrder) => {
            logger.debug('Received WebSocket order update:', newOrder);
            // Check if this is a status transition from CREATED to PREPARING
            setOrders(prevOrders => {
              const existingIndex = prevOrders.findIndex(o => o.id === newOrder.id);
              if (existingIndex >= 0) {
                const existingOrder = prevOrders[existingIndex];
                const isTransitioningToPreparing = existingOrder.status === 'CREATED' && newOrder.status === 'PREPARING';
                
                // Auto-print invoice when transitioning from CREATED to PREPARING
                if (isTransitioningToPreparing) {
                  invoiceService.printInvoice(newOrder, user, shop);
                }
                
                const updated = [...prevOrders];
                updated[existingIndex] = newOrder;
                return updated;
              } else {
                
                // Check if we already notified for this order
                if (!notifiedOrderIdsRef.current.has(newOrder.id)) {
                  notifiedOrderIdsRef.current.add(newOrder.id);
                  
                  // Move side effects to next tick
                  setTimeout(() => {
                    toast.success(t('barista.newOrderReceived', { table: newOrder.tableNumber }));
                    logger.debug('Current notification settings - will use fresh settings from hook');
                    logger.debug('About to call playNotificationSound...');
                    
                    // Call the notification function - it will get fresh settings
                    playNotificationSound();
                  }, 0);
                }
                
                return [newOrder, ...prevOrders];
              }
            });

            setIncomingOrders(prevIncoming => {
              const existingIndex = prevIncoming.findIndex(o => o.id === newOrder.id);
              if (existingIndex >= 0) {
                logger.debug('Updating existing QR order:', newOrder.id, 'Items:', newOrder.items.length);
                const updated = [...prevIncoming];
                updated[existingIndex] = newOrder;
                return updated;
              } else {
                // Only add QR orders to incoming orders
                if (newOrder.origin === 'QR') {
                  logger.debug('Adding new QR order to incoming orders:', newOrder.id);
                  return [newOrder, ...prevIncoming];
                }
                return prevIncoming;
              }
            });

            setManualOrders(prevManual => {
              const existingIndex = prevManual.findIndex(o => o.id === newOrder.id);
              if (existingIndex >= 0) {
                logger.debug('Updating existing manual order:', newOrder.id, 'Items:', newOrder.items.length);
                const updated = [...prevManual];
                updated[existingIndex] = newOrder;
                return updated;
              } else {
                // Only add manual orders to manual orders section
                if (newOrder.origin === 'MANUAL') {
                  logger.debug('Adding new manual order:', newOrder.id);
                  return [newOrder, ...prevManual];
                }
                return prevManual;
              }
            });
          });
        }).catch(error => {
          logger.error('WebSocket connection failed:', error);
          logger.info('Falling back to polling for real-time updates...');
        });
      }
    }

    return () => {
      websocketService.disconnect();
    };
  }, [shop, user, t, playNotificationSound]);

  // Fallback polling mechanism if WebSocket fails
  useEffect(() => {
    if (!shop || !user) return;

    const pollInterval = setInterval(async () => {
      try {
        const result = await orderService.getActiveOrders();
        if (result.success && Array.isArray(result.data)) {
          const currentOrders = result.data;
          
          // Check for new orders
          const previousOrderIds = new Set([...orders, ...incomingOrders, ...manualOrders].map(order => order.id));
          
          const newOrders = currentOrders.filter(order => !previousOrderIds.has(order.id));
          
          if (newOrders.length > 0) {
            logger.debug('Polling detected new orders:', newOrders.length);
            
            newOrders.forEach(newOrder => {
              if (!notifiedOrderIdsRef.current.has(newOrder.id)) {
                notifiedOrderIdsRef.current.add(newOrder.id);
                
                setTimeout(() => {
                  toast.success(t('barista.newOrderReceived', { table: newOrder.tableNumber }));
                  playNotificationSound();
                }, 0);
              }
            });
            
            // Update orders state
            setOrders(prevOrders => {
              const updated = [...prevOrders];
              currentOrders.forEach(currentOrder => {
                const existingIndex = updated.findIndex(o => o.id === currentOrder.id);
                if (existingIndex >= 0) {
                  updated[existingIndex] = currentOrder;
                } else {
                  updated.push(currentOrder);
                }
              });
              return updated;
            });
          }
        }
      } catch (error) {
  logger.error('Error polling for orders:', error);
      }
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(pollInterval);
  }, [shop, user, orders, incomingOrders, manualOrders, t, playNotificationSound]);

    const updateOrderStatus = async (orderId, status) => {
    try {
      // Find the current order to check if we're transitioning from CREATED to PREPARING
      const currentOrder = [...orders, ...incomingOrders, ...manualOrders].find(order => order.id === orderId);
      const isTransitioningToPreparing = currentOrder && currentOrder.status === 'CREATED' && status === 'PREPARING';
      
      const result = await orderService.updateOrderStatus(orderId, status);
      if (result.success) {
        setOrders(prevOrders => 
          prevOrders.map(order => 
            order.id === orderId ? result.data : order
          )
        );
        
        setIncomingOrders(prevIncoming => {
          if (status === 'COMPLETED' || status === 'CANCELLED') {
            return prevIncoming.filter(order => order.id !== orderId);
          } else {
            return prevIncoming.map(order => 
              order.id === orderId ? result.data : order
            );
          }
        });
        
        setManualOrders(prevManual => {
          if (status === 'COMPLETED' || status === 'CANCELLED') {
            return prevManual.filter(order => order.id !== orderId);
          } else {
            return prevManual.map(order => 
              order.id === orderId ? result.data : order
            );
          }
        });
        
                         // Auto-print invoice when transitioning from CREATED to PREPARING
                 if (isTransitioningToPreparing) {
                   logger.info('Auto-printing invoice for order:', result.data);
                   invoiceService.printInvoice(result.data, user, shop);
                 }
        
        toast.success(t('barista.orderStatusUpdated', { status: status }));
      } else {
        toast.error(result.error);
      }
    } catch (error) {
  logger.error('Failed to update order status:', error);
      toast.error(t('barista.failedToUpdateOrderStatus'));
    }
  };

  const addToCart = (product) => {
  logger.debug('Adding product to cart:', product.name, product.id);
    setCart(prevCart => {
      // Remove any duplicate items first
      const cleanCart = prevCart.filter(item => item.productId !== product.id);
      
      const existingItem = prevCart.find(item => item.productId === product.id);
      if (existingItem) {
        const updatedItem = { ...existingItem, quantity: existingItem.quantity + 1 };
        return [...cleanCart, updatedItem];
      } else {
        const newItem = {
          productId: product.id,
          productName: product.name,
          productPrice: product.price,
          productImage: product.imageUrl,
          quantity: 1
        };
        return [...cleanCart, newItem];
      }
    });
    toast.success(t('barista.productAddedToCart', { product: product.name }));
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.productId !== productId));
  };

  const updateCartQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(prevCart => 
      prevCart.map(item => 
        item.productId === productId 
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
  logger.debug('Clearing cart...');
    setCart([]);
    setSelectedTable('');
    setSelectedOrder(null);
  logger.debug('Cart cleared successfully');
  };

  const loadOrderIntoCart = (order) => {
    // Clear any existing cart first to prevent corruption
    setCart([]);
    setSelectedOrder(order);
    setSelectedTable(order.tableNumber.toString());
    
    const orderItems = order.items.map(item => ({
      productId: item.product.id,
      productName: item.product.name,
      productPrice: item.unitPrice,
      productImage: item.product.imageUrl,
      quantity: item.quantity
    }));
    
    setCart(orderItems);
    toast.success(t('barista.loadedOrderFromTable', { table: order.tableNumber }));
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.quantity * item.productPrice), 0);
  };

    const submitOrder = async () => {
    if (!selectedTable || cart.length === 0) {
      toast.error(t('barista.pleaseSelectTableAndAddItems'));
      return;
    }

    try {
      const orderData = {
        tableNumber: selectedTable,
        items: cart.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          unitPrice: item.productPrice
        }))
      };

      if (selectedOrder) {
        // Update existing order
  logger.debug('Updating order:', selectedOrder.id, 'with cart data:', orderData);
        const result = await orderService.updateOrder(selectedOrder.id, orderData);
        if (result.success) {
          logger.info('Order update successful:', result.data);
          toast.success(t('barista.orderUpdatedSuccessfully'));
          clearCart();
          // Force refresh orders to ensure we get the latest data
          await fetchOrders();
        } else {
          logger.error('Order update failed:', result.error);
          toast.error(result.error);
        }
      } else {
        // Create new order
        const result = await orderService.createOrder(orderData);
        if (result.success) {
          toast.success(t('barista.manualOrderCreatedSuccessfully'));
          clearCart();
          fetchOrders();
        } else {
          toast.error(result.error);
        }
      }
    } catch (error) {
  logger.error('Failed to submit order:', error);
      toast.error(t('barista.failedToSubmitOrder'));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'CREATED':
        return 'bg-gray-600';
      case 'PREPARING':
        return 'bg-orange-600';
      case 'READY':
        return 'bg-green-600';
      case 'COMPLETED':
        return 'bg-gray-500';
      case 'CANCELLED':
        return 'bg-red-600';
      default:
        return 'bg-gray-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'CREATED':
        return <Clock className="w-4 h-4" />;
      case 'PREPARING':
        return <AlertCircle className="w-4 h-4" />;
      case 'READY':
        return <CheckCircle className="w-4 h-4" />;
      case 'COMPLETED':
        return <CheckCircle className="w-4 h-4" />;
      case 'CANCELLED':
        return <X className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // const filteredOrders = orders.filter(order => 
  //   selectedStatus === 'all' || order.status === selectedStatus
  // );

  if (loading) {
    return (
          <div className={`${getThemeClasses()} flex items-center justify-center`}>
      <div className={`text-center ${settings.theme === 'light' ? 'text-gray-800' : 'text-white'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#a15507] mx-auto mb-4"></div>
                 <p className={`${settings.theme === 'light' ? 'text-gray-600' : 'text-gray-300'} ${getTextSizeClass()}`}>{t('common.loading')}</p>
      </div>
    </div>
    );
  }

  return (
    <div className={`${getThemeClasses()} flex`}>
      {/* Main Content Area - Left Side (70%) */}
      <div className="flex-1 flex flex-col">
                 {/* Header */}
                                       <header className={`${settings.theme === 'light' ? 'bg-white/80 border-gray-300' : settings.theme === 'coffee' ? 'bg-slate-800/90 border-slate-600' : 'bg-[#1f2937]/60 border-gray-700/50'} backdrop-blur-sm border-b px-6 py-4`}>
           <div className="flex items-center justify-between">
             <div className="flex items-center space-x-3">
               <Coffee className="text-[#a15507] w-8 h-8" />
                               <h1 className={`text-2xl font-bold ${settings.theme === 'light' ? 'text-gray-800' : 'text-white'} ${getTextSizeClass()}`}>{t('barista.title')}</h1>
                                     <span className={`${settings.theme === 'light' ? 'text-gray-600' : settings.theme === 'coffee' ? 'text-slate-300' : 'text-gray-300'}`}>- {shop?.name}</span>
             </div>
                           <div className="flex items-center space-x-6">
                                 <div className="text-right">
                                                                               <div className={`text-lg font-bold ${settings.theme === 'light' ? 'text-gray-800' : settings.theme === 'coffee' ? 'text-slate-100' : 'text-white'} uppercase ${getTextSizeClass()}`}>{user?.firstName} {user?.lastName}</div>
                 </div>
                 <div className="text-right">
                                                                               <div className={`text-sm ${settings.theme === 'light' ? 'text-gray-600' : settings.theme === 'coffee' ? 'text-slate-300' : 'text-gray-300'} ${getTextSizeClass()}`}>{t('menu.cart')} {t('menu.total')}</div>
                                       <div className={`text-xl font-bold text-[#a15507] ${getTextSizeClass()}`}>${getCartTotal().toFixed(2)}</div>
                 </div>
                <div className="flex items-center space-x-3">
                                     <button
                     onClick={() => setIsSettingsOpen(true)}
                                           className={`p-2 ${settings.theme === 'light' ? 'bg-gray-200 hover:bg-gray-300 text-gray-700' : settings.theme === 'coffee' ? 'bg-slate-700 hover:bg-slate-600 text-slate-200' : 'bg-gray-700 hover:bg-gray-600 text-gray-300'} rounded-lg transition-colors`}
                     title={t('barista.settings')}
                   >
                     <Settings className="w-5 h-5" />
                   </button>
                   <button
                     onClick={logout}
                                           className={`p-2 ${settings.theme === 'light' ? 'bg-red-200 hover:bg-red-300 text-red-700' : settings.theme === 'coffee' ? 'bg-red-700 hover:bg-red-600 text-red-200' : 'bg-red-700 hover:bg-red-600 text-red-300'} rounded-lg transition-colors`}
                     title={t('barista.logout')}
                   >
                     <LogOut className="w-5 h-5" />
                   </button>
                </div>
              </div>
           </div>
         </header>

                 {/* Main Content with Products and Orders */}
         <div className="flex-1 flex">
           {/* Products Section - Left (70%) */}
           <div className="w-4/5 p-6 overflow-y-auto">
            <div className="mb-6">
                                                           <h2 className={`text-xl font-bold ${settings.theme === 'light' ? 'text-gray-800' : settings.theme === 'coffee' ? 'text-slate-100' : 'text-white'} mb-4 ${getTextSizeClass()}`}>{t('dashboard.products')}</h2>
              
              {/* Category Filter */}
              <div className="flex flex-wrap gap-3 mb-6">
                {['all', 'COFFEE', 'TEA', 'SNACKS', 'DESSERTS', 'BEVERAGES'].map((category) => {
                  const count = category === 'all' 
                    ? products.length 
                    : products.filter(p => p.category === category).length;
                  
                  return (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                                             className={`px-4 py-3 rounded-lg font-medium transition-all duration-200 touch-manipulation ${getTextSizeClass()} ${
                         selectedCategory === category
                           ? 'bg-[#a15507] text-white shadow-lg scale-105'
                           : settings.theme === 'light' 
                             ? 'bg-gray-200 text-gray-700 hover:text-gray-900 hover:bg-gray-300'
                                                         : settings.theme === 'coffee'
                              ? 'bg-slate-700/60 text-slate-200 hover:text-slate-100 hover:bg-slate-700/80'
                              : 'bg-[#1f2937]/50 text-gray-300 hover:text-white hover:bg-[#1f2937]/70'
                       }`}
                    >
                      {category === 'all' ? t('menu.categories') : category}
                      <span className="ml-2 text-sm opacity-75">({count})</span>
                    </button>
                  );
                })}
              </div>
              
                             {/* Products Grid */}
               <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                 {products
                   .filter(product => selectedCategory === 'all' || product.category === selectedCategory)
                   .map((product) => (
                   <div
                     key={product.id}
                     onClick={() => addToCart(product)}
                                                                                       className={`${settings.theme === 'light' ? 'bg-white/80 border-gray-300 hover:bg-white/90' : settings.theme === 'coffee' ? 'bg-slate-800/70 border-slate-600 hover:bg-slate-800/90' : 'bg-[#1f2937]/50 border-gray-700/50 hover:bg-[#1f2937]/70'} backdrop-blur-sm rounded-xl cursor-pointer transition-all duration-200 hover:scale-105 touch-manipulation active:scale-95 relative overflow-hidden`}
                   >
                     <div className="aspect-square relative">
                       {product.imageUrl ? (
                         <img
                           src={getProductImageUrl(product.imageUrl)}
                           alt={product.name}
                           className="w-full h-full object-cover"
                           onError={(e) => {
                             e.target.style.display = 'none';
                             e.target.nextSibling.style.display = 'flex';
                           }}
                         />
                       ) : (
                         <div className="w-full h-full flex items-center justify-center bg-gray-800">
                           <Coffee className="w-16 h-16 text-gray-600" />
                         </div>
                       )}
                       {/* Product name overlay band */}
                       <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent flex items-end">
                         <div className="w-full p-3">
                                                       <h3 className={`${settings.theme === 'light' ? 'text-gray-800' : settings.theme === 'coffee' ? 'text-slate-100' : 'text-white'} font-semibold leading-tight ${getTextSizeClass()}`}>
                             {product.name}
                           </h3>
                         </div>
                       </div>
                     </div>
                   </div>
                 ))}
               </div>
            </div>
          </div>

                     {/* Orders Section - Right (30%) */}
                                                 <div className={`w-1/5 border-l ${settings.theme === 'light' ? 'border-gray-300' : settings.theme === 'coffee' ? 'border-slate-600' : 'border-gray-700/50'} flex flex-col`}>
             {/* QR Orders Panel */}
                                                         <div className={`flex-1 ${settings.theme === 'light' ? 'bg-gray-100/80' : settings.theme === 'coffee' ? 'bg-slate-800/70' : 'bg-[#1f2937]/60'}`}>
                                <div className={`p-4 border-b ${settings.theme === 'light' ? 'border-gray-300' : settings.theme === 'coffee' ? 'border-amber-600' : 'border-gray-700/50'}`}>
                                                                               <h2 className={`text-lg font-bold ${settings.theme === 'light' ? 'text-gray-800' : settings.theme === 'coffee' ? 'text-slate-100' : 'text-white'} mb-2 ${getTextSizeClass()}`}>{t('barista.qrOrders')}</h2>
                    <div className="flex justify-between items-center">
                                            <span className={`${settings.theme === 'light' ? 'text-gray-600' : settings.theme === 'coffee' ? 'text-slate-300' : 'text-gray-300'} ${getTextSizeClass()}`}>{t('barista.qrOrdersDesc')}</span>
                     <span className="text-[#a15507] font-semibold">{incomingOrders.length}</span>
                   </div>
                 </div>

               <div className="flex-1 overflow-y-auto">
                 {incomingOrders.length === 0 ? (
                   <div className="text-center py-8 px-4">
                     <Receipt className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                                                                                       <h3 className={`font-semibold ${settings.theme === 'light' ? 'text-gray-800' : settings.theme === 'coffee' ? 'text-slate-100' : 'text-white'} mb-1 ${getTextSizeClass()}`}>{t('barista.noOrders')}</h3>
                       <p className={`${settings.theme === 'light' ? 'text-gray-600' : settings.theme === 'coffee' ? 'text-slate-400' : 'text-gray-400'} ${getTextSizeClass()}`}>{t('barista.qrOrdersDesc')}</p>
                   </div>
                 ) : (
                   <div className="p-4 space-y-3">
                     {incomingOrders.map((order) => (
                       <div 
                         key={order.id} 
                         onClick={() => loadOrderIntoCart(order)}
                                                   className={`${settings.theme === 'light' ? 'bg-white/80 border-gray-300 hover:bg-white/90' : settings.theme === 'coffee' ? 'bg-slate-700/60 border-slate-500 hover:bg-slate-700/80' : 'bg-[#374151]/50 border-gray-600/50 hover:bg-[#374151]/70'} rounded-lg p-3 animate-pulse cursor-pointer transition-colors`}
                       >
                         <div className="flex justify-between items-start mb-2">
                           <div className="flex items-center space-x-2">
                             <div className="flex flex-col items-center justify-center w-8 h-8 bg-[#a15507]/20 border border-[#a15507] rounded-lg">
                               <Table className="w-3 h-3 text-[#a15507] mb-0.5" />
                               <span className="text-[#a15507] font-bold text-xs">
                                 {order.tableNumber}
                               </span>
                             </div>
                             <div>
                               <h4 className="text-white font-semibold text-sm">
                                 {t('barista.tableNumber', { number: order.tableNumber })}
                               </h4>
                               <p className="text-gray-400 text-xs">
                                 {formatTime(order.createdAt)}
                               </p>
                             </div>
                           </div>
                           <div className="flex items-center space-x-1">
                             {getStatusIcon(order.status)}
                             <span className={`px-2 py-1 rounded-full text-xs font-semibold text-white ${getStatusColor(order.status)}`}>
                               {order.status}
                             </span>
                           </div>
                         </div>

                         <div className="space-y-1 mb-2">
                           {order.items.map((item, index) => (
                             <div key={index} className="flex justify-between items-center">
                               <div>
                                 <p className="text-white font-medium text-xs">
                                   {item.product.name}
                                 </p>
                                 <p className="text-gray-400 text-xs">
                                   {t('menu.quantity')}: {item.quantity}
                                 </p>
                               </div>
                               <span className="text-[#a15507] font-semibold text-xs">
                                 ${item.totalPrice.toFixed(2)}
                               </span>
                             </div>
                           ))}
                         </div>

                                                   <div className="flex gap-1">
                            {order.status === 'CREATED' && (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateOrderStatus(order.id, 'PREPARING');
                                  }}
                                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-1 px-2 rounded text-xs font-medium touch-manipulation"
                                >
                                  {t('barista.startPreparing')}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateOrderStatus(order.id, 'CANCELLED');
                                  }}
                                  className="bg-red-600 hover:bg-red-700 text-white py-1 px-2 rounded text-xs font-medium touch-manipulation"
                                >
                                  {t('barista.cancel')}
                                </button>
                              </>
                            )}
                            {order.status === 'PREPARING' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateOrderStatus(order.id, 'READY');
                                }}
                                className="w-full bg-green-600 hover:bg-green-700 text-white py-1 px-2 rounded text-xs font-medium touch-manipulation"
                              >
                                {t('barista.markReady')}
                              </button>
                            )}
                            {order.status === 'READY' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateOrderStatus(order.id, 'COMPLETED');
                                }}
                                className="w-full bg-gray-600 hover:bg-gray-700 text-white py-1 px-2 rounded text-xs font-medium touch-manipulation"
                              >
                                {t('barista.markCompleted')}
                              </button>
                            )}
                          </div>
                       </div>
                     ))}
                   </div>
                 )}
               </div>
             </div>

             {/* Manual Orders Panel */}
                           <div className={`flex-1 ${settings.theme === 'light' ? 'bg-gray-200/80 border-gray-300' : settings.theme === 'coffee' ? 'bg-slate-700/50 border-slate-600' : 'bg-[#1f2937]/40 border-gray-700/50'} border-t`}>
               <div className={`p-4 border-b ${settings.theme === 'light' ? 'border-gray-300' : settings.theme === 'coffee' ? 'border-amber-600' : 'border-gray-700/50'}`}>
                                   <h2 className={`text-lg font-bold ${settings.theme === 'light' ? 'text-gray-800' : settings.theme === 'coffee' ? 'text-slate-100' : 'text-white'} mb-2 ${getTextSizeClass()}`}>{t('barista.manualOrders')}</h2>
                  <div className="flex justify-between items-center">
                    <span className={`${settings.theme === 'light' ? 'text-gray-600' : settings.theme === 'coffee' ? 'text-slate-300' : 'text-gray-300'} ${getTextSizeClass()}`}>{t('barista.manualOrders')}</span>
                   <span className="text-[#a15507] font-semibold">{manualOrders.length}</span>
                 </div>
               </div>

               <div className="flex-1 overflow-y-auto">
                 {manualOrders.length === 0 ? (
                   <div className="text-center py-8 px-4">
                     <Coffee className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                                                                 <h3 className={`font-semibold ${settings.theme === 'light' ? 'text-gray-800' : settings.theme === 'coffee' ? 'text-slate-100' : 'text-white'} mb-1 ${getTextSizeClass()}`}>{t('barista.noManualOrders')}</h3>
                       <p className={`${settings.theme === 'light' ? 'text-gray-600' : settings.theme === 'coffee' ? 'text-slate-400' : 'text-gray-400'} ${getTextSizeClass()}`}>{t('barista.createOrders')}</p>
                   </div>
                 ) : (
                   <div className="p-4 space-y-3">
                     {manualOrders.map((order) => (
                       <div 
                         key={order.id} 
                         onClick={() => loadOrderIntoCart(order)}
                                                   className={`${settings.theme === 'light' ? 'bg-white/80 border-gray-300 hover:bg-white/90' : settings.theme === 'coffee' ? 'bg-slate-700/60 border-slate-500 hover:bg-slate-700/80' : 'bg-[#374151]/50 border-gray-600/50 hover:bg-[#374151]/70'} rounded-lg p-3 cursor-pointer transition-colors`}
                       >
                         <div className="flex justify-between items-start mb-2">
                           <div className="flex items-center space-x-2">
                             <div className="flex flex-col items-center justify-center w-8 h-8 bg-[#a15507]/20 border border-[#a15507] rounded-lg">
                               <Table className="w-3 h-3 text-[#a15507] mb-0.5" />
                               <span className="text-[#a15507] font-bold text-xs">
                                 {order.tableNumber}
                               </span>
                             </div>
                             <div>
                               <h4 className="text-white font-semibold text-sm">
                                 {t('barista.tableNumber', { number: order.tableNumber })}
                               </h4>
                               <p className="text-gray-400 text-xs">
                                 {formatTime(order.createdAt)}
                               </p>
                             </div>
                           </div>
                           <div className="flex items-center space-x-1">
                             {getStatusIcon(order.status)}
                             <span className={`px-2 py-1 rounded-full text-xs font-semibold text-white ${getStatusColor(order.status)}`}>
                               {order.status}
                             </span>
                           </div>
                         </div>

                         <div className="space-y-1 mb-2">
                           {order.items.map((item, index) => (
                             <div key={index} className="flex justify-between items-center">
                               <div>
                                 <p className="text-white font-medium text-xs">
                                   {item.product.name}
                                 </p>
                                 <p className="text-gray-400 text-xs">
                                   {t('menu.quantity')}: {item.quantity}
                                 </p>
                               </div>
                               <span className="text-[#a15507] font-semibold text-xs">
                                 ${item.totalPrice.toFixed(2)}
                               </span>
                             </div>
                           ))}
                         </div>

                                                   <div className="flex gap-1">
                            {order.status === 'CREATED' && (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateOrderStatus(order.id, 'PREPARING');
                                  }}
                                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-1 px-2 rounded text-xs font-medium touch-manipulation"
                                >
                                  {t('barista.startPreparing')}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateOrderStatus(order.id, 'CANCELLED');
                                  }}
                                  className="bg-red-600 hover:bg-red-700 text-white py-1 px-2 rounded text-xs font-medium touch-manipulation"
                                >
                                  {t('barista.cancel')}
                                </button>
                              </>
                            )}
                            {order.status === 'PREPARING' && (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateOrderStatus(order.id, 'READY');
                                  }}
                                  className="flex-1 bg-green-600 hover:bg-green-700 text-white py-1 px-2 rounded text-xs font-medium touch-manipulation"
                                >
                                  {t('barista.markReady')}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateOrderStatus(order.id, 'CANCELLED');
                                  }}
                                  className="bg-red-600 hover:bg-red-700 text-white py-1 px-2 rounded text-xs font-medium touch-manipulation"
                                >
                                  {t('barista.cancel')}
                                </button>
                              </>
                            )}
                            {order.status === 'READY' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateOrderStatus(order.id, 'COMPLETED');
                                }}
                                className="w-full bg-gray-600 hover:bg-gray-700 text-white py-1 px-2 rounded text-xs font-medium touch-manipulation"
                              >
                                {t('barista.markCompleted')}
                              </button>
                            )}
                          </div>
                       </div>
                     ))}
                   </div>
                 )}
               </div>
             </div>
           </div>
        </div>
      </div>

             {/* Cart Sidebar - Right Side (30%) */}
               <div className={`w-1/3 max-w-md ${settings.theme === 'light' ? 'bg-white/80 border-gray-300' : settings.theme === 'coffee' ? 'bg-slate-800/90 border-slate-600' : 'bg-[#1f2937]/80 border-gray-700/50'} backdrop-blur-sm border-l flex flex-col h-screen`}>
         {/* Fixed Header Section */}
         <div className="flex-shrink-0">
           {/* Cart Header */}
                       <div className={`p-6 border-b ${settings.theme === 'light' ? 'border-gray-300' : settings.theme === 'coffee' ? 'border-slate-600' : 'border-gray-700/50'}`}>
             <div className="flex justify-between items-center mb-2">
                                                               <h2 className={`text-xl font-bold ${settings.theme === 'light' ? 'text-gray-800' : settings.theme === 'coffee' ? 'text-slate-100' : 'text-white'} ${getTextSizeClass()}`}>{t('menu.cart')}</h2>
               <button
                 onClick={clearCart}
                 className={`${settings.theme === 'light' ? 'text-red-600 hover:text-red-700' : 'text-red-400 hover:text-red-300'} text-sm font-medium transition-colors`}
               >
                 {t('common.clear')}
               </button>
             </div>
             <div className="flex justify-between items-center">
                                                               <span className={`${settings.theme === 'light' ? 'text-gray-600' : settings.theme === 'coffee' ? 'text-slate-300' : 'text-gray-300'} ${getTextSizeClass()}`}>{t('menu.cart')}: {cart.reduce((total, item) => total + item.quantity, 0)}</span>
               {selectedOrder && (
                 <div className="flex items-center space-x-2">
                   <span className="text-[#a15507] text-sm font-medium">
                     {t('barista.tableNumber', { number: selectedOrder.tableNumber })}
                   </span>
                   <span className={`px-2 py-1 rounded-full text-xs font-semibold text-white ${getStatusColor(selectedOrder.status)}`}>
                     {selectedOrder.status}
                   </span>
                 </div>
               )}
             </div>
           </div>

           {/* Table Selection - Fixed at top */}
           {cart.length > 0 && (
                           <div className={`p-6 border-b ${settings.theme === 'light' ? 'border-gray-300 bg-gray-100' : settings.theme === 'coffee' ? 'border-slate-600 bg-slate-700/60' : 'border-gray-700/50 bg-[#1f2937]/60'}`}>
                               <label className={`block text-lg font-bold ${settings.theme === 'light' ? 'text-gray-800' : settings.theme === 'coffee' ? 'text-slate-100' : 'text-white'} mb-4 ${getTextSizeClass()}`}>{t('dashboard.tables')}</label>
               <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                 {tables.map((table) => (
                   <button
                     key={table.id}
                     onClick={() => setSelectedTable(table.tableNumber.toString())}
                     className={`p-4 rounded-lg border-2 transition-all duration-200 hover:scale-105 ${
                       selectedTable === table.tableNumber.toString()
                         ? 'border-[#a15507] bg-[#a15507]/20 text-[#a15507]'
                         : settings.theme === 'light'
                           ? 'border-gray-400 bg-gray-200 text-gray-700 hover:border-gray-500 hover:bg-gray-300'
                                                       : settings.theme === 'coffee'
                              ? 'border-slate-500 bg-slate-600 text-slate-200 hover:border-slate-400 hover:bg-slate-500'
                              : 'border-gray-600 bg-[#374151] text-white hover:border-gray-500 hover:bg-[#374151]/80'
                     }`}
                   >
                     <div className="flex flex-col items-center space-y-2">
                       <Table className={`w-6 h-6 ${
                         selectedTable === table.tableNumber.toString()
                           ? 'text-[#a15507]'
                           : 'text-gray-400'
                       }`} />
                       <span className="font-semibold text-lg">
                         {table.tableNumber}
                       </span>
                       <span className="text-xs opacity-75">
                         {t('dashboard.tables')}
                       </span>
                     </div>
                   </button>
                 ))}
               </div>
               {!selectedTable && (
                                   <div className={`mt-4 p-4 ${settings.theme === 'light' ? 'bg-yellow-100 border-yellow-400' : settings.theme === 'coffee' ? 'bg-amber-600/20 border-amber-500' : 'bg-yellow-600/20 border-yellow-600'} border rounded-lg`}>
                   <div className="flex items-center space-x-2">
                                           <Table className={`w-5 h-5 ${settings.theme === 'light' ? 'text-yellow-600' : settings.theme === 'coffee' ? 'text-amber-400' : 'text-yellow-400'}`} />
                      <span className={`${settings.theme === 'light' ? 'text-yellow-700' : settings.theme === 'coffee' ? 'text-amber-400' : 'text-yellow-400'} font-medium ${getTextSizeClass()}`}>
                        {t('barista.selectTableToContinue')}
                      </span>
                   </div>
                 </div>
               )}
             </div>
           )}
         </div>

         {/* Scrollable Cart Items Section - Fixed height with proper overflow */}
         <div className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 400px)' }}>
           <div className="p-6">
             {cart.length === 0 ? (
               <div className="text-center py-12">
                 <ShoppingCart className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                                                     <h3 className={`text-lg font-semibold ${settings.theme === 'light' ? 'text-gray-800' : settings.theme === 'coffee' ? 'text-slate-100' : 'text-white'} mb-2 ${getTextSizeClass()}`}>{t('menu.cartEmpty')}</h3>
                   <p className={`${settings.theme === 'light' ? 'text-gray-600' : settings.theme === 'coffee' ? 'text-slate-400' : 'text-gray-400'} ${getTextSizeClass()}`}>{t('menu.tapProductToAdd')}</p>
               </div>
             ) : (
               <div className="space-y-4">
                 {cart.map((item) => (
                                       <div key={item.productId} className={`${settings.theme === 'light' ? 'bg-gray-100 border-gray-300' : settings.theme === 'coffee' ? 'bg-slate-700/60 border-slate-500' : 'bg-[#374151]/50 border-gray-600/50'} rounded-lg p-4 border`}>
                     <div className="flex items-center justify-between mb-3">
                       <div className="flex items-center space-x-3">
                         {item.productImage && (
                           <img
                             src={getProductImageUrl(item.productImage)}
                             alt={item.productName}
                             className="w-12 h-12 rounded-lg object-cover"
                             onError={(e) => {
                               e.target.style.display = 'none';
                             }}
                           />
                         )}
                         <div>
                                                       <h4 className={`${settings.theme === 'light' ? 'text-gray-800' : settings.theme === 'coffee' ? 'text-slate-100' : 'text-white'} font-semibold ${getTextSizeClass()}`}>{item.productName}</h4>
                           <p className="text-[#a15507] font-bold">${item.productPrice}</p>
                         </div>
                       </div>
                       <button
                         onClick={() => removeFromCart(item.productId)}
                         className="w-8 h-8 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center transition-colors touch-manipulation"
                       >
                         <Trash2 className="w-4 h-4" />
                       </button>
                     </div>
                     <div className="flex items-center justify-between">
                       <div className="flex items-center space-x-3">
                         <button
                           onClick={() => updateCartQuantity(item.productId, item.quantity - 1)}
                           disabled={item.quantity <= 1}
                           className="w-10 h-10 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-800 disabled:text-gray-500 text-white rounded-full flex items-center justify-center transition-colors touch-manipulation"
                         >
                           <Minus className="w-4 h-4" />
                         </button>
                                                   <span className={`${settings.theme === 'light' ? 'text-gray-800' : settings.theme === 'coffee' ? 'text-slate-100' : 'text-white'} font-bold min-w-[2rem] text-center ${getTextSizeClass()}`}>{item.quantity}</span>
                         <button
                           onClick={() => updateCartQuantity(item.productId, item.quantity + 1)}
                           className="w-10 h-10 bg-green-600 hover:bg-green-700 text-white rounded-full flex items-center justify-center transition-colors touch-manipulation"
                         >
                           <Plus className="w-4 h-4" />
                         </button>
                       </div>
                       <div className="text-right">
                                                   <p className={`${settings.theme === 'light' ? 'text-gray-800' : settings.theme === 'coffee' ? 'text-slate-100' : 'text-white'} font-bold ${getTextSizeClass()}`}>${(item.quantity * item.productPrice).toFixed(2)}</p>
                       </div>
                     </div>
                   </div>
                 ))}
               </div>
             )}
           </div>
         </div>

         {/* Fixed Footer Section - Always Visible at Bottom */}
                   <div className={`flex-shrink-0 border-t ${settings.theme === 'light' ? 'border-gray-300 bg-gray-100' : settings.theme === 'coffee' ? 'border-slate-600 bg-slate-700/60' : 'border-gray-700/50 bg-[#1f2937]/50'}`}>
           {/* Order Form */}
           <div className="p-6">
             {cart.length > 0 && (
               <>
                 <div className="flex justify-between items-center mb-4">
                                       <span className={`${settings.theme === 'light' ? 'text-gray-700' : settings.theme === 'coffee' ? 'text-slate-300' : 'text-gray-300'} ${getTextSizeClass()}`}>{t('barista.total')}:</span>
                                       <span className={`text-[#a15507] font-bold ${getTextSizeClass()}`}>${getCartTotal().toFixed(2)}</span>
                 </div>
                 
                 <button
                   onClick={submitOrder}
                   disabled={!selectedTable || cart.length === 0}
                                       className={`w-full py-4 bg-[#a15507] hover:bg-[#924e06] disabled:bg-gray-600 disabled:text-gray-400 text-white font-bold rounded-lg transition-all duration-200 touch-manipulation active:scale-95 ${getTextSizeClass()}`}
                 >
                   {selectedOrder ? t('barista.updateOrderForTable', { table: selectedTable }) : selectedTable ? t('barista.submitOrderForTable', { table: selectedTable }) : t('barista.selectTableToSubmit')}
                 </button>
               </>
             )}
           </div>
         </div>
       </div>

      <BaristaSettingsPanel 
        isOpen={isSettingsOpen} 
        onClose={() => setIsSettingsOpen(false)} 
      />
    </div>
  );
};

export default BaristaDashboard; 